import React, { useEffect, useState } from 'react'
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PendingIcon from '@mui/icons-material/Pending'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'
import StarIcon from '@mui/icons-material/Star'
import RepeatIcon from '@mui/icons-material/Repeat'
import BarChartIcon from '@mui/icons-material/BarChart'
import { makeGetRequest } from '../../utils/api'
import urls from '../../utils/urls'

// Styled components for dark theme matching list/detail pattern
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: 'linear-gradient(145deg, #1e1e2e 0%, #252540 100%)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(255,255,255,0.05)',
  height: '100%',
  '&:hover': {
    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.1)',
    transform: 'translateY(-4px)',
  },
}))

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 28px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  transition: 'all 0.3s ease',
}))

const Statistics = ({ onBack }) => {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchStatistics()
  }, [])

  const fetchStatistics = async () => {
    setLoading(true)
    try {
      const response = await makeGetRequest(urls.shoppingStatistics, {}, true)
      if (response?.status === 200) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching statistics:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0f0f1e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0f0f1e', pb: 4 }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ 
              mb: 2,
              borderRadius: 20,
              textTransform: 'none',
              fontWeight: 600,
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Retour aux listes
          </Button>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <BarChartIcon sx={{ fontSize: 40, color: '#667eea' }} />
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                Statistiques
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                Aperçu de vos listes de courses et votre bibliothèque
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Statistics Grid */}
        <Grid container spacing={3}>
          {/* Shopping Lists Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCartIcon sx={{ color: '#667eea' }} />
              Listes de Courses
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StyledCard>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <ShoppingCartIcon sx={{ fontSize: 60, color: '#667eea', mb: 2 }} />
                <Typography variant="h3" sx={{ color: '#667eea', fontWeight: 700, mb: 1 }}>
                  {stats?.total_lists || 0}
                </Typography>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                  Listes Totales
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  Toutes vos listes créées
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StyledCard>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <PendingIcon sx={{ fontSize: 60, color: '#ff9800', mb: 2 }} />
                <Typography variant="h3" sx={{ color: '#ff9800', fontWeight: 700, mb: 1 }}>
                  {stats?.active_lists || 0}
                </Typography>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                  Listes Actives
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  En cours ou à faire
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StyledCard>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
                <Typography variant="h3" sx={{ color: '#4caf50', fontWeight: 700, mb: 1 }}>
                  {stats?.completed_lists || 0}
                </Typography>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                  Listes Terminées
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  Courses complétées
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Library Statistics Section */}
          <Grid item xs={12} sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LibraryBooksIcon sx={{ color: '#667eea' }} />
              Ma Bibliothèque
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StyledCard>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <LibraryBooksIcon sx={{ fontSize: 60, color: '#2196f3', mb: 2 }} />
                <Typography variant="h3" sx={{ color: '#2196f3', fontWeight: 700, mb: 1 }}>
                  {stats?.total_library_items || 0}
                </Typography>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                  Articles Total
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  Articles favoris
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StyledCard>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <StarIcon sx={{ fontSize: 60, color: '#ffc107', mb: 2 }} />
                <Typography variant="h3" sx={{ color: '#ffc107', fontWeight: 700, mb: 1 }}>
                  {stats?.default_items || 0}
                </Typography>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                  Auto-Ajout
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  Ajoutés automatiquement
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StyledCard>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <RepeatIcon sx={{ fontSize: 60, color: '#9c27b0', mb: 2 }} />
                <Typography variant="h3" sx={{ color: '#9c27b0', fontWeight: 700, mb: 1 }}>
                  {stats?.recurring_items || 0}
                </Typography>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                  Articles Récurrents
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  Avec fréquence définie
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Additional Info */}
        <Box sx={{ mt: 5 }}>
          <StyledCard>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                💡 Conseils pour mieux faire vos courses
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <StarIcon sx={{ color: '#ffc107', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Ajoutez les articles fréquents à votre bibliothèque avec "Auto-Ajout" pour gagner du temps
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <RepeatIcon sx={{ color: '#9c27b0', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Définissez une fréquence pour les articles récurrents comme le lait ou le pain
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <LibraryBooksIcon sx={{ color: '#2196f3', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Organisez les articles par rayon pour faire vos courses plus rapidement
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <ShoppingCartIcon sx={{ color: '#667eea', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Copiez vos listes précédentes pour créer rapidement de nouvelles listes similaires
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Utilisez la barre de progression pour suivre vos achats en temps réel
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <BarChartIcon sx={{ color: '#ff9800', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Consultez vos statistiques régulièrement pour optimiser vos habitudes d'achat
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>
        </Box>
      </Container>
    </Box>
  )
}

export default Statistics
